import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { SbModalState } from '../modal-state.service';
import { SearchSelectOptions } from '../modal.model';
import { AddressDTO, API, Pagination } from 'src/app/services/api/api';
import { BehaviorSubject } from 'rxjs';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { ModalNavigationComponent } from '../modal-navigation-component';
import { ModalNavigationService } from '../modal-navigation.service';
import { CullAtomLookup } from 'src/app/components/culls/CullAtomLookup';
import { TranslateService } from '@ngx-translate/core';
import { StringTransformationService } from '../../../transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-search-select-modal',
  templateUrl: './sb-search-select-modal.component.html',
  styleUrls: ['./sb-search-select-modal.component.scss'],
  standalone: false,
})
export class SbSearchSelectModalComponent<T> implements OnInit, ModalNavigationComponent {
  private readonly stringTransformation = inject(StringTransformationService);

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.pagination.value.page = val;
  }

  get empty(): boolean {
    return this._empty;
  }

  set empty(val: boolean) {
    this._empty = val;
    if (this._empty) {
      this.selectedOptions = null;
    }
  }

  constructor(
    private state: SbModalState,
    private api: API,
    private errorHandler: ErrorService,
    public util: UtilService,
    private modalNavigation: ModalNavigationService,
    public translate: TranslateService
  ) {}

  options: SearchSelectOptions<T>;
  @Input()
  templateId: string;
  selectedOptions: any[];

  @Input()
  datatype: string;
  @Input()
  attributeType: string;
  @Input()
  cullAtomLookup: CullAtomLookup;
  @Input()
  singleMode: boolean;

  @Output()
  selectionChanged: EventEmitter<any> = new EventEmitter<any>();

  results: any[] = [];

  displayResults: any[] = [];

  loading = true;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  searchStr: string;
  search: string;
  exactMatch: boolean = false;

  maxSize: number;
  boundaryLinks: boolean = true;

  resultCount: number;
  currentCount: number;
  collectionSize: number;

  pagination: BehaviorSubject<Pagination>;

  paginationString: string;

  _page: number = 1;

  sizes: number[] = [5, 8, 10, 15, 20, 25];

  paginationInitialized: boolean = false;

  _empty: boolean;

  isInModalContext: boolean;

  ngOnInit() {
    this.selectedOptions = [];
    if (this.templateId) {
      this.options = this.state.options.get(this.templateId) as SearchSelectOptions<T>;

      if (this.options.initial) {
        for (const e of this.options.initial) {
          this.selectedOptions.push(e);
        }
      }

      this.datatype = this.options.datatype;
      this.attributeType = this.options.attributeType;
      this.cullAtomLookup = this.options.cullAtomLookup;
    } else if (this.isInModalContext) {
      this.options = this.modalNavigation.getCurrentNavigation().data;
      this.datatype = this.options.datatype;
      this.attributeType = this.options.attributeType;
      this.cullAtomLookup = this.options.cullAtomLookup;
      if (this.options.initial) {
        for (const e of this.options.initial) {
          this.selectedOptions.push(e);
          switch (this.attributeType) {
            case 'LOCATION':
              this.modalNavigation.getSharedData().locations = this.selectedOptions;
              this.modalNavigation.getSharedData().noLocationSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'CHANNEL':
              this.modalNavigation.getSharedData().channels = this.selectedOptions;
              this.modalNavigation.getSharedData().noChannelSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'POOL':
              this.modalNavigation.getSharedData().pools = this.selectedOptions;
              this.modalNavigation.getSharedData().noPoolSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'AREA_CATEGORY':
              this.modalNavigation.getSharedData().areaCategories = this.selectedOptions;
              this.modalNavigation.getSharedData().noAreaCategoriesSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'PRODUCT_CATEGORY':
              this.modalNavigation.getSharedData().productCategories = this.selectedOptions;
              this.modalNavigation.getSharedData().noProductCategoriesSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'TEMPLATE_GROUP':
              this.modalNavigation.getSharedData().templateGroups = this.selectedOptions;
              this.modalNavigation.getSharedData().noTemplateGroupsSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'NOTIFICATION_CATEGORY':
              this.modalNavigation.getSharedData().notificationCategories = this.selectedOptions;
              this.modalNavigation.getSharedData().noNotiticationCategoriesSelected =
                !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'CAMPAIGNTYPE':
              this.modalNavigation.getSharedData().campaignTypes = this.selectedOptions;
              this.modalNavigation.getSharedData().noCampaignTypesSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'COMPANY':
              this.modalNavigation.getSharedData().companies = this.selectedOptions;
              this.modalNavigation.getSharedData().noCompanySelected = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'CLIENTCULL':
              this.modalNavigation.getSharedData().clientCull = this.selectedOptions;
              this.modalNavigation.getSharedData().noClientCull = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
            case 'CONTENTCULL':
              this.modalNavigation.getSharedData().contentCull = this.selectedOptions;
              this.modalNavigation.getSharedData().noContentCull = !this.selectedOptions || this.selectedOptions.length <= 0;
              break;
          }
        }
      }

      this.datatype = this.options.datatype;
      this.attributeType = this.options.attributeType;
    }

    if (this.options) {
      if (this.options.singlemode) {
        this.singleMode = this.options.singlemode;
      }
      if (this.options.isEmpty) {
        this.empty = true;
      }
    }

    if (!this.selectedOptions) {
      this.selectedOptions = [];
    }

    this.initPagination();
  }

  getBodyClass(): string {
    if (this.templateId) {
      return 'modal-body';
    }
    return '';
  }

  ok() {
    if (this.checkOkEnabled()) {
      this.state.modals.get(this.templateId).close({
        values: this.selectedOptions ? this.selectedOptions : [],
        empty: this.empty,
        cancel: false,
      });
    }
  }

  checkOkEnabled() {
    return true;
    /* TODO check if code needed
    if (this.selectedOptions) {
      return this.selectedOptions ;
    }
    return false;*/
  }

  cancel() {
    this.state.modals.get(this.templateId).close({
      value: null,
      cancel: true,
    });
  }

  add(anySelection: any) {
    if (!this.selectedOptions) {
      this.selectedOptions = [];
    }
    if (this.singleMode) {
      this.selectedOptions.splice(0);
      this.selectedOptions.push(anySelection);
      this.triggerEventIfNotModal();
    } else if (!this.selectedOptions.some(option => option.id === anySelection.id)) {
      this.selectedOptions.push(anySelection);
      this.triggerEventIfNotModal();
    }
    if (this.selectedOptions.length > 0) {
      this.empty = false;
    }
  }

  remove(anySelection) {
    const index = this.selectedOptions.findIndex(value => value.id === anySelection?.id);
    if (this.selectedOptions.length >= 0 && index >= 0) {
      this.selectedOptions.splice(index, 1);
      this.triggerEventIfNotModal();
    }
  }

  triggerEventIfNotModal() {
    if (!this.templateId) {
      this.selectionChanged.emit(this.selectedOptions);
    }
    if (this.isInModalContext) {
      let goback: boolean = true;
      switch (this.attributeType) {
        case 'LOCATION':
          this.modalNavigation.getSharedData().locations = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noLocationSelected = false;
            this.modalNavigation.getSharedData().emptyLocationSelected = false;
          } else {
            this.modalNavigation.getSharedData().noLocationSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'CHANNEL':
          this.modalNavigation.getSharedData().channels = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noChannelSelected = false;
            this.modalNavigation.getSharedData().emptyChannelSelected = false;
          } else {
            this.modalNavigation.getSharedData().noChannelSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'POOL':
          this.modalNavigation.getSharedData().pools = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noPoolSelected = false;
            this.modalNavigation.getSharedData().emptyPoolSelected = false;
          } else {
            this.modalNavigation.getSharedData().noPoolSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'AREA_CATEGORY':
          this.modalNavigation.getSharedData().areaCategories = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noAreaCategoriesSelected = false;
            this.modalNavigation.getSharedData().emptyAreaCategoriesSelected = false;
          } else {
            this.modalNavigation.getSharedData().noAreaCategoriesSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'PRODUCT_CATEGORY':
          this.modalNavigation.getSharedData().productCategories = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noProductCategoriesSelected = false;
            this.modalNavigation.getSharedData().emptyProductCategoriesSelected = false;
          } else {
            this.modalNavigation.getSharedData().noProductCategoriesSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;

        case 'TEMPLATE_GROUP':
          this.modalNavigation.getSharedData().templateGroups = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noTemplateGroupsSelected = false;
            this.modalNavigation.getSharedData().emptyTemplateGroupsSelected = false;
          } else {
            this.modalNavigation.getSharedData().noTemplateGroupsSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'NOTIFICATION_CATEGORY':
          this.modalNavigation.getSharedData().notificationCategories = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noNotificationCategoriesSelected = false;
            this.modalNavigation.getSharedData().emptyNotificationCategoriesSelected = false;
          } else {
            this.modalNavigation.getSharedData().noNotificationCategoriesSelected =
              !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'CAMPAIGNTYPE':
          this.modalNavigation.getSharedData().campaignTypes = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().nocampaignTypeSelected = false;
            this.modalNavigation.getSharedData().emptycampaignTypeSelected = false;
          } else {
            this.modalNavigation.getSharedData().nocampaignTypeSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'COMPANY':
          this.modalNavigation.getSharedData().company = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noCompanySelected = false;
            this.modalNavigation.getSharedData().emptyCompanySelected = false;
          } else {
            this.modalNavigation.getSharedData().noCompanySelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'CLIENTCULL':
          this.modalNavigation.getSharedData().clientCull = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noClientCullSelected = false;
            this.modalNavigation.getSharedData().emptyClientCullSelected = false;
          } else {
            this.modalNavigation.getSharedData().noClientCullSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        case 'CONTENTCULL':
          this.modalNavigation.getSharedData().contentCull = this.selectedOptions;
          if (this.empty) {
            this.modalNavigation.getSharedData().noContentCullSelected = false;
            this.modalNavigation.getSharedData().emptyContentCullSelected = false;
          } else {
            this.modalNavigation.getSharedData().noContentCullSelected = !this.selectedOptions || this.selectedOptions.length <= 0;
          }
          break;
        default:
          goback = false;
          break;
      }
      if (goback) {
        this.modalNavigation.goBack();
      }
    }
  }

  getClass(anySelection: any) {
    if (!anySelection.id) {
      if (this.selectedOptions.indexOf(anySelection) >= 0) {
        return 'selected';
      }
    } else if (this.selectedOptions.findIndex(value => value.id === anySelection.id)) {
      return 'selected';
    }
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.loading = true;
    let req: boolean = true;
    let obs;

    if (this.attributeType) {
      switch (this.attributeType) {
        case 'LOCATION':
          obs = this.api.ss_fm_Location_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'CHANNEL':
          obs = this.api.ss_nm_Channel_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'POOL':
          obs = this.api.ss_nm_Pool_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'AREA_CATEGORY':
          obs = this.api.ca_md_AreaCategory_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'PRODUCT_CATEGORY':
          obs = this.api.pm_ProductCategory_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'TEMPLATE_GROUP':
          obs = this.api.mm_templating_provider_TemplateGroup_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'DEAL':
          obs = this.api.shared_Deal_retrieveAll(
            {
              // search: this.util.encodeSearchQueryParam(this.exactMatch, this.search)
            },
            this.pagination
          );
          break;
        case 'SSP_CONTRACT':
          obs = this.api.shared_SSPContract_retrieveAll(
            {
              // search: this.util.encodeSearchQueryParam(this.exactMatch, this.search)
            },
            this.pagination
          );
          break;
        case 'NOTIFICATION_CATEGORY':
          obs = this.api.shared_Notification_retrieveCategories(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'CAMPAIGNTYPE':
          obs = this.api.ca_md_CampaignType_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'ROLE':
          obs = this.api.ca_am_Role_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'PERMISSION':
          obs = this.api.shared_Permission_retrieveAll();
          break;
        case 'COMPANY':
          if (this.datatype === 'COMPANY_MANAGES_LOCATION') {
            obs = this.api.shared_Company_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
                managesLocations: true,
              },
              this.pagination
            );
          } else {
            obs = this.api.shared_Company_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
              },
              this.pagination
            );
          }
          break;
        case 'CLIENTCULL':
          obs = this.api.cs_Cull_retrieveAll(
            {
              type: 'ClientCull',
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        case 'CONTENTCULL':
          obs = this.api.cs_Cull_retrieveAll(
            {
              type: 'ContentCull',
              search: this.stringTransformation.encodeSearchQueryParam(this.exactMatch, this.search),
            },
            this.pagination
          );
          break;
        default:
          req = false;
          break;
      }
    } else if (this.cullAtomLookup) {
      obs = this.api.cs_Cull_selectionLookup(
        this.cullAtomLookup.lookup,
        this.cullAtomLookup.cullAtom,
        {
          search: this.stringTransformation.encodeSearchQueryParam(false, this.search),
        },
        this.pagination
      );
    }

    if (req) {
      obs.subscribe(
        data => {
          this.results = data;
          this.displayResults = data;
          this.currentCount = data.length;
          this.paginationString = this.stringTransformation.getPaginationString(
            this.page,
            this.maxSize,
            this.currentCount,
            this.resultCount,
            'items'
          );
          this.paginationInitialized = true;
          this.loading = false;
        },
        error => {
          this.errorHandler.errorHandler(error);
        }
      );
    }
  }

  dosearch($event) {
    this.search = $event;
    if (this.attributeType === 'PERMISSION') {
      if (!this.search) {
        this.displayResults = [];
        this.displayResults = this.results;
      } else {
        const res = [];
        for (const perm of this.results) {
          const translated: string = this.translate.instant('permission.' + perm);
          if (
            perm === this.search ||
            translated.toLowerCase() === this.search.toLowerCase() ||
            perm.toLowerCase().includes(this.search.toLowerCase()) ||
            translated.toLowerCase().includes(this.search.toLowerCase())
          ) {
            res.push(perm);
          }
        }

        this.displayResults = [];
        this.displayResults = res;
      }
    } else {
      this.requestDebounced();
    }
  }

  initPagination() {
    this.pagination = new BehaviorSubject<Pagination>({
      page: this.page,
      count: this.maxSize ? this.maxSize : 8,
      pages: -1,
      resultcount: -1,
    });
    this.pagination.subscribe((e: Pagination) => {
      this.collectionSize = e.resultcount;
      this.maxSize = e.count;
      this.resultCount = e.resultcount;
      // this.page = e.page;
    });

    this.requestDebounced();
  }

  setMaxSize(size) {
    this.maxSize = size;
    this.pagination.value.count = size;
    this.requestDebounced();
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  getName(item: any): string {
    if (item) {
      if (this.cullAtomLookup) {
        if (this.cullAtomLookup?.cullAtom?.type?.toLowerCase()?.includes('attribute')) {
          return typeof item.display === 'string' ? item.display : item.display.name ? item.display.name : item;
        } else {
          switch (this.cullAtomLookup.lookup) {
            case 'PATH_BASED_STRUCTURE_CULL_ATOM_FIELD':
            case 'PATH_BASED_GEO_CULL_ATOM_FIELD':
            case 'DATE_TIME_PART_CULL_ATOM_PART':
              return this.translate.instant('lang.globals.' + item.display.charAt(0).toLowerCase() + item.display.substring(1));
            default:
              return typeof item.display === 'string' ? item.display : item.display.name ? item.display.name : item;
          }
        }
      } else {
        switch (this.attributeType) {
          case 'LOCATION':
          case 'COMPANY':
            return this.stringTransformation.locationOrCompanyToShortString(item);
          case 'CHANNEL':
          case 'POOL':
          case 'AREA_CATEGORY':
          case 'PRODUCT_CATEGORY':
          case 'TEMPLATE_GROUP':
          case 'CAMPAIGNTYPE':
          case 'NOTIFICATION_CATEGORY':
          case 'DEAL':
            return item.name && item.identifier ? item.name + '(' + item.identifier + ')' : item.name ? item.name : item.identifier;
          case 'SSP_CONTRACT':
            return item.ssp.name;
          case 'ROLE':
            return item.name;
          case 'PERMISSION':
            return item;
          case 'CLIENTCULL':
          case 'CONTENTCULL':
            return item.name;
          default:
            return 'FIXME';
        }
      }
    } else {
      return '';
    }
  }

  getDescription(item: any): string {
    if (item) {
      if (this.cullAtomLookup) {
        return item.value;
      } else {
        switch (this.attributeType) {
          case 'LOCATION':
          case 'COMPANY':
            if (item.address) {
              const address: AddressDTO = item.address;
              let result = '';

              result = this.stringTransformation.addressToFullString(address);

              return result;
            }
            break;
          case 'CHANNEL':
          case 'POOL':
          case 'CAMPAIGNTYPE':
            return item.description ? item.description : '';
          case 'AREA_CATEGORY':
            return item.countAreas ? (item.countAreas === 1 ? item.countAreas + ' Area' : item.countAreas + ' Areas') : '0 Areas';
          case 'PRODUCT_CATEGORY':
            return item.products
              ? item.products.length === 1
                ? item.products.length + ' Product'
                : item.products.length + ' Products'
              : '0 Products';
          case 'TEMPLATE_GROUP':
            return item.variables.length + ' ' + this.translate.instant('lang.globals.templateVariables');
          case 'NOTIFICATION_CATEGORY':
            return '';
          case 'DEAL':
            return item.sspContract ? item.sspContract.ssp.name : '';
          case 'SSP_CONTRACT':
            return item.id ? item.id : '';
          case 'ROLE':
            return item?.namedescription;
          case 'PERMISSION':
            return '';
          case 'CLIENTCULL':
            return '';
          case 'CONTENTCULL':
            return '';
          default:
            return 'FIXME';
        }
      }
    } else {
      return '';
    }
  }
}
